import { FormGroup } from '@angular/forms';
import { MultiselectObjectManagerComponentBase } from '../../../core/component/multiselect-object-manager/multiselect-object-manager.component';
import { FilterDefinitionItem, FilterDefinitionItemLocalDate } from '../../../core/model/filter-definition-item';
import { CommandDispatcher } from '../../../core/service/command/command-dispatcher.service';
import { MessageService } from '../../../core/service/message.service';
import { NavigationService } from '../../../core/service/navigation.service';
import { mailCommand } from '../../../message/model/const/command';
import { leaseAgreementCommand } from '../../command/lease-agreement.command';
import { LeaseAgreementStatus, LeasePeriodKind, TerminationMonitoringStatus } from '../../model/const/enums';
import { Contract } from '../../model/contract';
import { ContractService } from '../../service/base/contract';
import { ContractCommandHandlerResolver } from '../../service/command/contract-command-handler-resolver.service';
import { ActivatedRoute } from '@angular/router';
import { Directive, inject } from '@angular/core';
import { UserAccountService } from '../../../common/service/user-account.service';
import { Features, ModuleNames } from '../../../common/model/account-definition';
import { getMonthName } from '../../../core/helper/month';
import { PagedObjectManagerComponent } from '../../../core/component/paged-object-manager/paged-object-manager';
import { DataService } from '../../../core/service/base/data.service';
import { IndexClause } from '../../model/lease-row';
import { GetInvoiceDeliveryMethodIcon, InvoiceDeliveryMethod, InvoiceDeliveryMethodToString } from '../../../invoice/model/const/enum';
import { CompanyService } from '../../service/data/company.service';
import { PropertyService } from '../../service/data/property.service';
import { standardCommand } from '../../../core/model/command';
import { ContactService } from '../../service/data/contact.service';
import { LeaseAgreementListService } from '../../service/data/lease-agreement-list.service';
import { Observable } from 'rxjs';
import { ContractListDrilldownQuery, parseContractListDrilldownQuery } from '../../helper/contract-list-drilldown';
import { filterStateSignature, withIgnoreSaveOnLoad } from '../../../core/service/list-query-params';
import { LocalDate } from '../../../core/model/local-date';

@Directive()
export abstract class ContractManagerPagedBase<TModel extends Contract> extends PagedObjectManagerComponent<TModel> {
  constructor(
    contractDataService: ContractService<TModel>,
    dispatcher: CommandDispatcher,
    handlerResolver: ContractCommandHandlerResolver,
    messageService: MessageService,
    navigationService: NavigationService,
    private _userAccount: UserAccountService,
    private _companyService: CompanyService,
    private _propertyService: PropertyService,
    activatedRoute?: ActivatedRoute,
    private readonly _leaseAgreementListService?: LeaseAgreementListService<TModel>
  ) {
    super(contractDataService, messageService, navigationService, dispatcher, handlerResolver, _leaseAgreementListService);

    this.AddSubscription(
      this._userAccount.current.subscribe({
        next: account => {
          if (account.hasAccess(ModuleNames.Messages, Features.ManageESignatures) && account.hasLeaseAgreementWriteAccess()) {
            this.addContextMenuItemDefinition('COMMON.COMMAND.SEND_FOR_DIGITAL_SIGNING', leaseAgreementCommand.sendForEsignDocument);
          }
          if (account.hasLeaseAgreementWriteAccess()) {
            this.addContextMenuItemDefinition('LEASE_AGREEMENT.COMMAND.APPROVE', leaseAgreementCommand.approve);
          } else {
            this.allowNew = false;
          }
          if (account.hasLeaseAgreementWriteAccess()) {
            this.addContextMenuItemDefinition('LEASE_AGREEMENT.COMMAND.WITHDRAW_APPROVAL', leaseAgreementCommand.withdrawApproval);
          }
          if (account.hasLeaseAgreementWriteAccess()) {
            this.addContextMenuItemDefinition('LEASE_AGREEMENT.COMMAND.CREATE_INVOICE', leaseAgreementCommand.createInvoice);
          }
          this.addContextMenuItemDefinition('LEASE_AGREEMENT.COMMAND.SHOW_INVOICES', leaseAgreementCommand.showInvoices);
          if (account.hasLeaseAgreementWriteAccess()) {
            this.addContextMenuItemDefinition('LEASE_AGREEMENT.COMMAND.TERMINATE', leaseAgreementCommand.terminate);
          }
          if (account.hasLeaseAgreementWriteAccess()) {
            this.addContextMenuItemDefinition('LEASE_AGREEMENT.COMMAND.WITHDRAW_TERMINATION', leaseAgreementCommand.withdrawTermination);
          }
          this.addContextMenuItemDefinition('LEASE_AGREEMENT.COMMAND.PRINT_TERMINATION_FORM', leaseAgreementCommand.printTerminationForm);
          if (account.hasLeaseAgreementWriteAccess()) {
            this.addContextMenuItemDefinition('LEASE_AGREEMENT.COMMAND.LINK_DOCUMENT', leaseAgreementCommand.linkDocument);
          }
          this.addContextMenuItemDefinition('LEASE_AGREEMENT.COMMAND.SHOW_DOCUMENTS', leaseAgreementCommand.showDocuments);

          if (account.hasAccess(ModuleNames.Messages, Features.ManageEmail)) {
            this.addListMenuItem('LEASE_AGREEMENT.COMMAND.SEND_EMAIL', mailCommand.sendTenantEmail);
          }
          if (account.hasAccess(ModuleNames.Messages, Features.ManageMail)) {
            this.addListMenuItem('LEASE_AGREEMENT.COMMAND.SEND_LETTER', mailCommand.sendTenantMail);
          }
          if (account.hasAccess(ModuleNames.Messages, Features.ManageSms)) {
            this.addListMenuItem('LEASE_AGREEMENT.COMMAND.SEND_SMS', mailCommand.sendTenantSMS);
          }
        },
      })
    );

    if (activatedRoute) {
      this.AddSubscription(
        activatedRoute.queryParamMap.subscribe(map => {
          if (map.has('status')) {
            const param = map.get('status');
            if (param) {
              const status = LeaseAgreementStatus[param[0].toUpperCase() + param.substring(1)];
              if (status) {
                this._status = status;
              }
            }
          }
          if (map.has('terminationMonitoringStatus')) {
            const param = map.get('terminationMonitoringStatus');
            if (param) {
              const terminationMonitoringStatus = TerminationMonitoringStatus[param[0].toUpperCase() + param.substring(1)];
              if (terminationMonitoringStatus) {
                this._terminationMonitoringStatus = terminationMonitoringStatus;
              }
            }
          }
          if (map.has('fixedTermExpirationStatus')) {
            const fixedTermExpirationStatus = Number(map.get('fixedTermExpirationStatus'));
            if (Number.isInteger(fixedTermExpirationStatus) && fixedTermExpirationStatus >= 3 && fixedTermExpirationStatus <= 12) {
              this._fixedTermExpirationStatus = fixedTermExpirationStatus;
            }
          }
          if (map.has('securityOfTenureExpirationStatus')) {
            const securityOfTenureExpirationStatus = Number(map.get('securityOfTenureExpirationStatus'));
            if (Number.isInteger(securityOfTenureExpirationStatus) && securityOfTenureExpirationStatus >= 3 && securityOfTenureExpirationStatus <= 12) {
              this._securityOfTenureExpirationStatus = securityOfTenureExpirationStatus;
            }
          }
          this._drilldownQuery = parseContractListDrilldownQuery(map);
          this._status = this._drilldownQuery.status;
          this._terminationMonitoringStatus = this._drilldownQuery.terminationMonitoringStatus;
          this._fixedTermExpirationStatus = this._drilldownQuery.fixedTermExpirationStatus;
          this._securityOfTenureExpirationStatus = this._drilldownQuery.securityOfTenureExpirationStatus;
          this._ignoreSaveOnLoad = this._drilldownQuery.isExternal;
          this._initialExternalFilterSignature = null;
          if (this._filterDefinitionSource) this.addFilterDefinitions(this._filterDefinitionSource);
        })
      );
    }
  }

  private _status: LeaseAgreementStatus;
  private _terminationMonitoringStatus: TerminationMonitoringStatus;
  private _fixedTermExpirationStatus: number;
  private _securityOfTenureExpirationStatus: number;
  private _drilldownQuery: ContractListDrilldownQuery = { selectedFields: [], isExternal: false };
  private _ignoreSaveOnLoad = false;
  private _initialExternalFilterSignature: string | null = null;
  private _filterDefinitionSource: any;
  private readonly _contactService = inject(ContactService);
  statusSelection: { name: string; value: string }[];

  ngOnInit() {
    const filterDefinition$: Observable<any> = this._leaseAgreementListService
      ? this._leaseAgreementListService.getFilterDefinition()
      : (<ContractService<TModel>>this.dataService).getFilterDefinition();

    this.AddSubscription(
      filterDefinition$.subscribe(definition => {
        this._filterDefinitionSource = definition;
        this.addFilterDefinitions(definition);
      })
    );

    this.AddSubscription(
      this._userAccount.current.subscribe({
        next: account => {
          if (account.hasLeaseAgreementWriteAccess()) {
            this.addContextMenuItemDefinition('EDIT_BTN', standardCommand.edit);
            this.addContextMenuItemDefinition('DELETE_BTN', standardCommand.delete);
          } else {
            this.addContextMenuItemDefinition('EDIT_BTN', standardCommand.edit);
          }
        },
      })
    );
  }

  override onFilterChange(filterState: any): void {
    if (this._ignoreSaveOnLoad && filterState?.selectedFields) {
      const signature = filterStateSignature(filterState);
      if (this._initialExternalFilterSignature === null) this._initialExternalFilterSignature = signature;
      else if (signature !== this._initialExternalFilterSignature) this._ignoreSaveOnLoad = false;
    }
    super.onFilterChange(filterState);
  }

  protected override preparePageFilter(filter: any): any {
    return withIgnoreSaveOnLoad(filter, this._ignoreSaveOnLoad);
  }

  protected addFilterDefinitions(definition: any) {
    if (this._drilldownQuery.isExternal) definition = { ...definition, selectedFields: [...this._drilldownQuery.selectedFields] };
    const isFixedTermExpirationOverview = this._drilldownQuery.fixedTermExpirationStatus !== undefined;
    const isSecurityOfTenureExpirationOverview = this._drilldownQuery.securityOfTenureExpirationStatus !== undefined;
    if (isFixedTermExpirationOverview || isSecurityOfTenureExpirationOverview) {
      Object.assign(definition, {
        selectedFields: [...new Set([...(definition.selectedFields ?? []), 'status', isFixedTermExpirationOverview ? 'fixedTermExpirationStatus' : 'securityOfTenureExpirationStatus'])],
        status: 9,
        company: [],
        property: [],
        tenantContactId: '',
        contactName: '',
        tenantContactIdentification: '',
        street: '',
        city: '',
        leaseAgreementNumber: '',
        popularName: '',
        rentalId: '',
        leasePeriodType: null,
        article: '',
        leaseAgreementRowIndexClause: null,
        terminationMonitoringStatus: 0,
        fixedTermExpirationStatus: isFixedTermExpirationOverview ? this._fixedTermExpirationStatus : 0,
        securityOfTenureExpirationStatus: isSecurityOfTenureExpirationOverview ? this._securityOfTenureExpirationStatus : 0,
        invoiceDeliveryMethod: '',
        subleaseStatus: 0,
        building: [],
        companyCustomPropertiesTextValues: [],
        companyCustomPropertiesDateValues: [],
        companyCustomPropertiesNumericValues: [],
        companyCustomPropertiesBooleanValues: [],
        rentalObjectCustomPropertiesTextValues: [],
        rentalObjectCustomPropertiesDateValues: [],
        rentalObjectCustomPropertiesNumericValues: [],
        rentalObjectCustomPropertiesBooleanValues: [],
        propertyCustomPropertiesTextValues: [],
        propertyCustomPropertiesDateValues: [],
        propertyCustomPropertiesNumericValues: [],
        propertyCustomPropertiesBooleanValues: [],
        buildingCustomPropertiesTextValues: [],
        buildingCustomPropertiesDateValues: [],
        buildingCustomPropertiesNumericValues: [],
        buildingCustomPropertiesBooleanValues: [],
      });
    }

    this.defaultFilters = definition.defaultFields;
    const articles = definition.articles.map(x => {
      return { caption: x.toString(), value: x.id };
    });
    articles.splice(0, 0, { caption: 'ALL_TEXT', value: '' });
    const yearSelection: { caption: string; value: number }[] = [];
    const currentYear = new Date().getFullYear();
    for (let offset = -2; offset <= 4; offset++)
      yearSelection.push({ caption: (currentYear + offset).toString(), value: currentYear + offset });

    const monthSelection: { caption: string; value: number }[] = [];
    for (let monthNumber = 1; monthNumber <= 12; monthNumber++)
      monthSelection.push({ caption: getMonthName(monthNumber), value: monthNumber });
    const overviewQueryExists = this._drilldownQuery.isExternal;
    this.filterDefinition = [
      new FilterDefinitionItem(
        'LEASE_AGREEMENT.STATUS',
        'status',
        this._drilldownQuery.terminationMonitoringStatus !== undefined || this._drilldownQuery.status !== undefined
          ? this._drilldownQuery.status !== undefined && this._drilldownQuery.terminationMonitoringStatus === undefined
            ? this._drilldownQuery.status
            : 0
          : isFixedTermExpirationOverview || isSecurityOfTenureExpirationOverview
            ? 9
            : definition.status,
        [
          { caption: 'ALL_TEXT', value: 0 },
          { caption: 'CONST.LEASE_AGREEMENT_STATUS_ACTIVE', value: 9 },
          { caption: 'CONST.LEASE_AGREEMENT_STATUS_DRAFT', value: LeaseAgreementStatus.Draft },
          { caption: 'CONST.LEASE_AGREEMENT_STATUS_COMING', value: LeaseAgreementStatus.Coming },
          { caption: 'CONST.LEASE_AGREEMENT_STATUS_VALID', value: LeaseAgreementStatus.Valid },
          { caption: 'CONST.LEASE_AGREEMENT_STATUS_TERMINATED', value: LeaseAgreementStatus.Terminated },
          { caption: 'CONST.LEASE_AGREEMENT_STATUS_EXPIRED', value: LeaseAgreementStatus.Expired },
          { caption: 'CONST.LEASE_AGREEMENT_STATUS_VOIDED', value: LeaseAgreementStatus.Voided },
        ],
        definition.selectedFields.includes('status')
      ),

      new FilterDefinitionItem(
        'COMPANY.LIST_HEADER',
        'company',
        overviewQueryExists ? [] : definition.company,
        this._companyService,
        definition.selectedFields.includes('company'),
        true
      ),
      new FilterDefinitionItem(
        'RENTAL_OBJECT_FORM.PROPERTY_LABEL',
        'property',
        overviewQueryExists ? [] : definition.property,
        this._propertyService,
        definition.selectedFields.includes('property'),
        true
      ),
      this._leaseAgreementListService
        ? new FilterDefinitionItem(
            'LEASE_AGREEMENT.TENANT_NAME',
            'tenantContactId',
            overviewQueryExists ? '' : definition.tenantContactId,
            this._contactService,
            definition.selectedFields.includes('tenantContactId'),
            false,
            { displayFormat: 'contractTenantFilter' }
          )
        : new FilterDefinitionItem(
            'LEASE_AGREEMENT.TENANT_NAME',
            'tenant',
            overviewQueryExists ? '' : definition.tenant,
            null,
            definition.selectedFields.includes('tenant')
          ),
      new FilterDefinitionItem(
        'TENANT.CIVIC_ORG_NUMBER',
        'tenantContactIdentification',
        overviewQueryExists ? '' : definition.tenantContactIdentification,
        null,
        definition.selectedFields.includes('tenantContactIdentification')
      ),
      new FilterDefinitionItem(
        'RENTAL_OBJECT_LIST.STREET_COLUMN',
        'street',
        overviewQueryExists ? '' : definition.street,
        null,
        definition.selectedFields.includes('street')
      ),
      new FilterDefinitionItem(
        'RENTAL_OBJECT_LIST.CITY_COLUMN',
        'city',
        overviewQueryExists ? '' : definition.city,
        null,
        definition.selectedFields.includes('city')
      ),
      new FilterDefinitionItem(
        'LEASE_AGREEMENT.LEASE_AGREEMENT_NUMBER',
        'leaseAgreementNumber',
        overviewQueryExists ? '' : definition.leaseAgreementNumber,
        null,
        definition.selectedFields.includes('leaseAgreementNumber')
      ),
      new FilterDefinitionItem(
        'COMMON.NOTE',
        'note',
        overviewQueryExists ? '' : definition.note,
        null,
        definition.selectedFields.includes('note')
      ),
      new FilterDefinitionItem(
        'RENTAL_OBJECT_FORM.RENTAL_OBJECT_NUMBER_LABEL',
        'rentalId',
        overviewQueryExists ? '' : definition.rentalId,
        null,
        definition.selectedFields.includes('rentalId')
      ),
      new FilterDefinitionItem(
        'ARTICLE.EDIT_HEADER',
        'article',
        overviewQueryExists ? '' : definition.article,
        articles,
        definition.selectedFields.includes('article')
      ),
      new FilterDefinitionItem(
        'LEASE_AGREEMENT_NOTICE_PERIOD_FORM.LEASE_PERIOD_LABEL',
        'leasePeriodType',
        overviewQueryExists ? null : definition.leasePeriodType,
        [
          { caption: 'ALL_TEXT', value: null },
          { caption: 'CONST.LEASE_PERIOD_AUTOMATIC_EXTENSION', value: LeasePeriodKind.AutomaticExtension },
          { caption: 'CONST.LEASE_PERIOD_LIMITED_PERIOD', value: LeasePeriodKind.LimitedPeriod },
          { caption: 'CONST.LEASE_PERIOD_UNTIL_FUTHER_NOTICE', value: LeasePeriodKind.UntilFurtherNotice },
        ],
        definition.selectedFields.includes('leasePeriodType')
      ),
      new FilterDefinitionItem(
        'LEASE_AGREEMENT_ROW.INDEX_CLAUSE',
        'leaseAgreementRowIndexClause',
        overviewQueryExists ? null : definition.leaseAgreementRowIndexClause,
        [
          { caption: 'ALL_TEXT', value: null },
          { caption: 'LEASE_AGREEMENT_ROW.INDEX_CLAUSE_KPI80_FAST', value: IndexClause.FastighetsAgarna },
          { caption: 'LEASE_AGREEMENT_ROW.INDEX_CLAUSE_KPI80_KOMMUNFORBUNDET', value: IndexClause.Kommunforbundet },
          { caption: 'LEASE_AGREEMENT_ROW.INDEX_CLAUSE_KPI80_MINIMUM3UNITS', value: IndexClause.KPI80Minimum3Units },
          { caption: 'LEASE_AGREEMENT_ROW.INDEX_CLAUSE_KPI80_PLUS', value: IndexClause.KPI80Plus },
          { caption: 'LEASE_AGREEMENT_ROW.INDEX_CLAUSE_KPI80_PLUS_QUARTER', value: IndexClause.KPI80PlusQuarter },
          { caption: 'LEASE_AGREEMENT_ROW.INDEX_CLAUSE_KPI2020_FAST', value: IndexClause.KPI2020FastighetsAgarna },
          { caption: 'LEASE_AGREEMENT_ROW.INDEX_CLAUSE_KPI2020_PLUS', value: IndexClause.KPI2020Plus },
          { caption: 'LEASE_AGREEMENT_ROW.INDEX_CLAUSE_KPI2020_PLUS_QUARTER', value: IndexClause.KPI2020PlusQuarter },
          { caption: 'Anpassad klausul', value: IndexClause.KPI2020Custom }
        ],
        definition.selectedFields.includes('leaseAgreementRowIndexClause')
      ),
      new FilterDefinitionItem(
        'COMMON.DISPLAY_YEAR_RENT_YEAR',
        'rentFromYear',
        definition.rentFromYear,
        yearSelection,
        definition.selectedFields.includes('rentFromYear')
      ),
      new FilterDefinitionItem(
        'COMMON.DISPLAY_YEAR_RENT_MONTH',
        'rentFromMonth',
        definition.rentFromMonth,
        monthSelection,
        definition.selectedFields.includes('rentFromMonth')
      ),
      new FilterDefinitionItem(
        'RENTAL_OBJECT_LIST.ROUND_RENTS',
        'roundRent',
        definition.roundRent,
        [
          { caption: 'YES_TEXT', value: true },
          { caption: 'NO_TEXT', value: false },
        ],
        definition.selectedFields.includes('roundRent')
      ),
      new FilterDefinitionItem(
        'LEASE_AGREEMENT.TERMINATION_MONITORING',
        'terminationMonitoringStatus',
        this._drilldownQuery.terminationMonitoringStatus !== undefined
          ? this._drilldownQuery.terminationMonitoringStatus
          : overviewQueryExists
            ? 0
            : definition.terminationMonitoringStatus,
        [
          { caption: 'COMMON.NOT_SPECIFIED', value: 0 },
          { caption: 'CONST.TERMINATION_MONITORING_STATUS_ONE_MONTH', value: 1 },
          { caption: 'CONST.TERMINATION_MONITORING_STATUS_TWO_MONTHS', value: 2 },
          { caption: 'CONST.TERMINATION_MONITORING_STATUS_THREE_MONTHS', value: 3 },
          { caption: 'CONST.TERMINATION_MONITORING_STATUS_FOUR_MONTHS', value: 4 },
          { caption: 'CONST.TERMINATION_MONITORING_STATUS_FIVE_MONTHS', value: 5 },
          { caption: 'CONST.TERMINATION_MONITORING_STATUS_SIX_MONTHS', value: 6 },
          { caption: 'CONST.TERMINATION_MONITORING_STATUS_SEVEN_MONTHS', value: 7 },
          { caption: 'CONST.TERMINATION_MONITORING_STATUS_EIGHT_MONTHS', value: 8 },
          { caption: 'CONST.TERMINATION_MONITORING_STATUS_NINE_MONTHS', value: 9 },
          { caption: 'CONST.TERMINATION_MONITORING_STATUS_TEN_MONTHS', value: 10 },
          { caption: 'CONST.TERMINATION_MONITORING_STATUS_ELEVEN_MONTHS', value: 11 },
          { caption: 'CONST.TERMINATION_MONITORING_STATUS_TWELVE_MONTHS', value: 12 },
        ],
        this._terminationMonitoringStatus ? true : definition.selectedFields.includes('terminationMonitoringStatus')
      ),
      new FilterDefinitionItemLocalDate(
        'DASHBOARD_LIST_FILTERS.CONTRACT_FROM_DATE_START',
        'fromDateStart',
        this._drilldownQuery.fromDateStart ?? (overviewQueryExists ? LocalDate.None : definition.fromDateStart ?? LocalDate.None),
        definition.selectedFields.includes('fromDateStart')
      ),
      new FilterDefinitionItemLocalDate(
        'DASHBOARD_LIST_FILTERS.CONTRACT_FROM_DATE_END',
        'fromDateEnd',
        this._drilldownQuery.fromDateEnd ?? (overviewQueryExists ? LocalDate.None : definition.fromDateEnd ?? LocalDate.None),
        definition.selectedFields.includes('fromDateEnd')
      ),
      new FilterDefinitionItemLocalDate(
        'DASHBOARD_LIST_FILTERS.CONTRACT_TERMINATION_DATE_START',
        'terminationDateStart',
        this._drilldownQuery.terminationDateStart ?? (overviewQueryExists ? LocalDate.None : definition.terminationDateStart ?? LocalDate.None),
        definition.selectedFields.includes('terminationDateStart')
      ),
      new FilterDefinitionItemLocalDate(
        'DASHBOARD_LIST_FILTERS.CONTRACT_TERMINATION_DATE_END',
        'terminationDateEnd',
        this._drilldownQuery.terminationDateEnd ?? (overviewQueryExists ? LocalDate.None : definition.terminationDateEnd ?? LocalDate.None),
        definition.selectedFields.includes('terminationDateEnd')
      ),
      new FilterDefinitionItem(
        'LEASE_AGREEMENT.FIXED_TERM',
        'fixedTermExpirationStatus',
        this._fixedTermExpirationStatus ? this._fixedTermExpirationStatus : overviewQueryExists ? 0 : definition.fixedTermExpirationStatus,
        [
          { caption: 'COMMON.NOT_SPECIFIED', value: 0 },
          { caption: 'LEASE_AGREEMENT.FIXED_TERM_EXPIRES_WITHIN_THREE_MONTHS', value: 3 },
          { caption: 'LEASE_AGREEMENT.FIXED_TERM_EXPIRES_WITHIN_FOUR_MONTHS', value: 4 },
          { caption: 'LEASE_AGREEMENT.FIXED_TERM_EXPIRES_WITHIN_FIVE_MONTHS', value: 5 },
          { caption: 'LEASE_AGREEMENT.FIXED_TERM_EXPIRES_WITHIN_SIX_MONTHS', value: 6 },
          { caption: 'LEASE_AGREEMENT.FIXED_TERM_EXPIRES_WITHIN_SEVEN_MONTHS', value: 7 },
          { caption: 'LEASE_AGREEMENT.FIXED_TERM_EXPIRES_WITHIN_EIGHT_MONTHS', value: 8 },
          { caption: 'LEASE_AGREEMENT.FIXED_TERM_EXPIRES_WITHIN_NINE_MONTHS', value: 9 },
          { caption: 'LEASE_AGREEMENT.FIXED_TERM_EXPIRES_WITHIN_TEN_MONTHS', value: 10 },
          { caption: 'LEASE_AGREEMENT.FIXED_TERM_EXPIRES_WITHIN_ELEVEN_MONTHS', value: 11 },
          { caption: 'LEASE_AGREEMENT.FIXED_TERM_EXPIRES_WITHIN_TWELVE_MONTHS', value: 12 },
        ],
        this._fixedTermExpirationStatus ? true : definition.selectedFields.includes('fixedTermExpirationStatus')
      ),
      new FilterDefinitionItem(
        'LEASE_AGREEMENT.SECURITY_OF_TENURE_EXPIRES',
        'securityOfTenureExpirationStatus',
        this._securityOfTenureExpirationStatus ? this._securityOfTenureExpirationStatus : overviewQueryExists ? 0 : definition.securityOfTenureExpirationStatus,
        [
          { caption: 'COMMON.NOT_SPECIFIED', value: 0 },
          { caption: 'LEASE_AGREEMENT.SECURITY_OF_TENURE_EXPIRES_WITHIN_THREE_MONTHS', value: 3 },
          { caption: 'LEASE_AGREEMENT.SECURITY_OF_TENURE_EXPIRES_WITHIN_FOUR_MONTHS', value: 4 },
          { caption: 'LEASE_AGREEMENT.SECURITY_OF_TENURE_EXPIRES_WITHIN_FIVE_MONTHS', value: 5 },
          { caption: 'LEASE_AGREEMENT.SECURITY_OF_TENURE_EXPIRES_WITHIN_SIX_MONTHS', value: 6 },
          { caption: 'LEASE_AGREEMENT.SECURITY_OF_TENURE_EXPIRES_WITHIN_SEVEN_MONTHS', value: 7 },
          { caption: 'LEASE_AGREEMENT.SECURITY_OF_TENURE_EXPIRES_WITHIN_EIGHT_MONTHS', value: 8 },
          { caption: 'LEASE_AGREEMENT.SECURITY_OF_TENURE_EXPIRES_WITHIN_NINE_MONTHS', value: 9 },
          { caption: 'LEASE_AGREEMENT.SECURITY_OF_TENURE_EXPIRES_WITHIN_TEN_MONTHS', value: 10 },
          { caption: 'LEASE_AGREEMENT.SECURITY_OF_TENURE_EXPIRES_WITHIN_ELEVEN_MONTHS', value: 11 },
          { caption: 'LEASE_AGREEMENT.SECURITY_OF_TENURE_EXPIRES_WITHIN_TWELVE_MONTHS', value: 12 },
        ],
        this._securityOfTenureExpirationStatus ? true : definition.selectedFields.includes('securityOfTenureExpirationStatus')
      ),
      new FilterDefinitionItem(
        'LEASE_AGREEMENT.INVOICE_DELIVERY_METHOD',
        'invoiceDeliveryMethod',
        overviewQueryExists ? '' : definition.invoiceDeliveryMethod,
        [
          { caption: 'ALL_TEXT', value: '' },
          { caption: InvoiceDeliveryMethodToString(InvoiceDeliveryMethod.Kivra), value: InvoiceDeliveryMethod.Kivra, icon: GetInvoiceDeliveryMethodIcon(InvoiceDeliveryMethod.Kivra) },
          { caption: InvoiceDeliveryMethodToString(InvoiceDeliveryMethod.EInvoiceB2C), value: InvoiceDeliveryMethod.EInvoiceB2C, icon: GetInvoiceDeliveryMethodIcon(InvoiceDeliveryMethod.EInvoiceB2C) },
          { caption: InvoiceDeliveryMethodToString(InvoiceDeliveryMethod.EmailWithPDF), value: InvoiceDeliveryMethod.EmailWithPDF, icon: GetInvoiceDeliveryMethodIcon(InvoiceDeliveryMethod.EmailWithPDF) },
          { caption: InvoiceDeliveryMethodToString(InvoiceDeliveryMethod.EmailWithoutPDF), value: InvoiceDeliveryMethod.EmailWithoutPDF, icon: GetInvoiceDeliveryMethodIcon(InvoiceDeliveryMethod.EmailWithoutPDF) },
          { caption: InvoiceDeliveryMethodToString(InvoiceDeliveryMethod.Mail), value: InvoiceDeliveryMethod.Mail, icon: GetInvoiceDeliveryMethodIcon(InvoiceDeliveryMethod.Mail) },
          { caption: InvoiceDeliveryMethodToString(InvoiceDeliveryMethod.Manual), value: InvoiceDeliveryMethod.Manual, icon: GetInvoiceDeliveryMethodIcon(InvoiceDeliveryMethod.Manual) },
          { caption: InvoiceDeliveryMethodToString(InvoiceDeliveryMethod.EInvoiceB2B), value: InvoiceDeliveryMethod.EInvoiceB2B, icon: GetInvoiceDeliveryMethodIcon(InvoiceDeliveryMethod.EInvoiceB2B) },
          { caption: InvoiceDeliveryMethodToString(InvoiceDeliveryMethod.Avy), value: InvoiceDeliveryMethod.Avy, icon: GetInvoiceDeliveryMethodIcon(InvoiceDeliveryMethod.Avy) },
          { caption: InvoiceDeliveryMethodToString(InvoiceDeliveryMethod.NoDelivery), value: InvoiceDeliveryMethod.NoDelivery, icon: GetInvoiceDeliveryMethodIcon(InvoiceDeliveryMethod.NoDelivery) },
        ],
        definition.selectedFields.includes('invoiceDeliveryMethod')
      ),
    ];
  }
}
